import { useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Languages, Loader2 } from "lucide-react";

/**
 * Version anglaise d'un contenu (produit, prestataire, catégories de chaînes, modèle) : le français à gauche, l'anglais
 * éditable à droite. « Traduire (IA) » remplit les champs anglais vides ; l'équipe relit puis enregistre.
 * `pairs` : [{ key, label?, fr, en }] ; `onSave({ [key]: en })` enregistre (champ vide = texte français chez le client).
 */
export default function TranslationDialog({ title, pairs, onSave, onClose }) {
  const [en, setEn] = useState(() => Object.fromEntries(pairs.map((p) => [p.key, p.en || ""])));
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = pairs.filter((p) => p.fr?.trim() && !en[p.key]?.trim());

  const translate = async () => {
    setTranslating(true);
    try {
      const { data } = await api.post("/ai/translate", { texts: empty.map((p) => p.fr) });
      setEn((cur) => ({ ...cur, ...Object.fromEntries(empty.map((p, i) => [p.key, data.texts[i] || ""])) }));
      toast.success("Traduction proposée : relisez avant d'enregistrer");
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(Object.fromEntries(pairs.map((p) => [p.key, en[p.key].trim()])));
      toast.success("Version anglaise enregistrée");
      onClose();
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="translation-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <DialogDescription>Présentée aux clients anglophones. Un champ laissé vide reste en français.</DialogDescription>
        <div className="space-y-4 py-2">
          {pairs.map((p) => {
            const long = (p.fr || "").length > 70 || (p.fr || "").includes("\n");
            const Field = long ? Textarea : Input;
            return (
              <div key={p.key} className="grid gap-2 sm:grid-cols-2" data-testid={`translation-row-${p.key}`}>
                <div>
                  {p.label && <p className="text-xs font-medium text-muted-foreground mb-1">{p.label}</p>}
                  <p className="text-sm whitespace-pre-line bg-muted/40 border border-border rounded-sm px-3 py-2">{p.fr || "—"}</p>
                </div>
                <div>
                  {p.label && <p className="text-xs font-medium text-muted-foreground mb-1">Anglais</p>}
                  <Field value={en[p.key]} onChange={(e) => setEn((cur) => ({ ...cur, [p.key]: e.target.value }))}
                    placeholder="Texte français conservé" aria-label={`Anglais : ${p.label || p.fr}`}
                    data-testid={`translation-en-${p.key}`} className="rounded-sm" {...(long ? { rows: 3 } : {})} />
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={translate} disabled={translating || !empty.length}
            data-testid="translation-ai" className="rounded-sm" title={empty.length ? "" : "Tout est traduit"}>
            {translating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Languages className="w-4 h-4 mr-1.5" />}
            Traduire (IA){empty.length ? ` · ${empty.length}` : ""}
          </Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="translation-save" className="rounded-sm bg-primary hover:bg-primary/90">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
